import { Divider, IconButton, Typography, CircularProgress } from "@mui/material";
import { Box } from "@mui/system";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";
import addMethodIcon from "../../assets/addMethodIcon.svg";
import AddIcon from "@mui/icons-material/Add";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import ReceiptIcon from "@mui/icons-material/Receipt";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import billingHistoryIcon from "../../assets/billingHistoryIcon.svg";
import manageMembership from "../../assets/manageMembership.svg";
import { useSafeAccessAuth0Token } from "src/hooks/useSafeAccessAuth0Token";
import { getActiveSubscription } from "src/api/getActiveSubscription";
import { useState } from "react";
import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import { isPlatform } from "@ionic/react";
import LogOutButton from "../LogComponents/LogOutButton";
import DeleteAccountButton from "../DeleteAccountButton/DeleteAccountButton";
import { useDoLogout } from "src/utils/useDoLogout";
import { isNativeIapSupported } from "src/services/iap";
import { getProdFrontendFallbackUrl } from "src/config/frontendUrls";

export const PaymentMethodMenu = ({ onClose, onChangeContent, onBack, onCancelMembershipClick, onDeleteAccountClick, userSubscription }) => {
    const { getSafeAccessAuth0Token } = useSafeAccessAuth0Token();
    const [isLoadingCancelMembership, setIsLoadingCancelMembership] = useState(false);
    const doLogOut = useDoLogout();

    // Check if running on native platform (iOS/Android)
    const isNativePlatform = Capacitor.isNativePlatform();
    const isHybridPlatform = isPlatform("hybrid");
    const isRunningNatively = isNativePlatform || isHybridPlatform;

    // Function to open web app in Capacitor Browser for native platforms
    const openWebAppInBrowser = async (path: string = "/home", queryParams: string = "") => {
        if (!isRunningNatively) {
            // On web, use normal navigation
            return false;
        }

        try {
            // Get base URL from environment variable
            const baseUrl = process.env.REACT_APP_FRONTEND_URI_WEB || getProdFrontendFallbackUrl();
            // Remove trailing slash and /home if present
            const cleanBaseUrl = baseUrl.replace(/\/$/, "").replace(/\/home$/, "");
            const fullUrl = `${cleanBaseUrl}${path}${queryParams ? `?${queryParams}` : ""}`;

            console.log("📱 Opening web app in Capacitor Browser:", fullUrl);

            // Open in Capacitor Browser
            await Browser.open({ url: fullUrl, presentationStyle: "popover" });
            return true;
        } catch (error) {
            console.error("Error opening Capacitor Browser:", error);
            return false;
        }
    };

    const handleManageMembershipClick = async () => {
        // Native iOS/Android: stay in-app and open ManageMembership (StoreKit / Google Play Billing).
        if (isNativeIapSupported()) {
            onChangeContent("manageMembership");
            return;
        }

        // Other native (e.g. Android): open web app in Capacitor Browser with payment drawer
        if (isRunningNatively) {
            const opened = await openWebAppInBrowser("/home", "openPayment=manageMembership&fromApp=true");
            if (opened) {
                onClose();
                return;
            }
        }

        onChangeContent("manageMembership");
    };

    const handleCancelMembershipClick = async () => {
        // Cancel membership works directly in the app (both native and web)
        // No need to open Capacitor Browser for cancellation
        if (isLoadingCancelMembership) return; // Prevent multiple clicks

        try {
            setIsLoadingCancelMembership(true);
            const accessAuth0Token = await getSafeAccessAuth0Token();
            if (!accessAuth0Token) {
                console.error("Failed to get access token");
                setIsLoadingCancelMembership(false);
                onChangeContent("cancelMembership", null);
                return;
            }

            const response = await getActiveSubscription(accessAuth0Token);
            let subscriptionData = null;

            if (response.success && response.hasActiveSubscription && response.subscription?.expirationDate) {
                // Format the expiration date
                const expirationDate = new Date(response.subscription.expirationDate);
                const formattedDate = expirationDate.toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                });
                subscriptionData = {
                    expiryDate: formattedDate,
                    hasActiveSubscription: true,
                };
            } else {
                subscriptionData = {
                    expiryDate: null,
                    hasActiveSubscription: false,
                };
            }

            // Pass data to parent component
            if (onCancelMembershipClick) {
                onCancelMembershipClick(subscriptionData);
            }
            onChangeContent("cancelMembership", subscriptionData);
        } catch (error) {
            console.error("Error fetching subscription:", error);
            const subscriptionData = {
                expiryDate: null,
                hasActiveSubscription: false,
            };
            if (onCancelMembershipClick) {
                onCancelMembershipClick(subscriptionData);
            }
            onChangeContent("cancelMembership", subscriptionData);
        } finally {
            setIsLoadingCancelMembership(false);
        }
    };

    const showCancelMembership = userSubscription !== "free";
    return (
        <>
            <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center" p={1}>
                <IconButton aria-label="close" onClick={onClose} sx={{ visibility: "hidden" }}>
                    <ArrowBackIcon sx={{ color: `${PureLightThemeCaaS.colors.alpha.black[100]}` }} />
                </IconButton>
                <Typography variant="h4" pl={1} color={`${PureLightThemeCaaS.colors.alpha.black[100]}`} textAlign={"center"}>
                    Account Settings
                </Typography>
                <Box display="flex" flexDirection="row" justifyContent="flex-end" alignItems="center">
                    <IconButton aria-label="close" onClick={onClose}>
                        <CloseIcon sx={{ color: `${PureLightThemeCaaS.colors.alpha.black[100]}` }} />
                    </IconButton>
                </Box>
            </Box>
            {/* Divider for separation between the title and the cards */}
            <Divider
                sx={{
                    margin: {
                        xs: "auto",
                        md: "0px",
                    },
                    width: "100%",
                    bgcolor: "#000000",
                    my: 0.5,
                }}
            />
            <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="flex-start"
                pt={3}
                sx={{
                    pl: {
                        xs: 1,
                        md: 5,
                    },
                    pr: {
                        xs: 1,
                        md: 5,
                    },
                }}
                gap={2}
            >
                {/* <Box onClick={() => onChangeContent("addMethod")} display="flex" flexDirection="row" justifyContent="center" alignItems="center" pl={1} gap={1} sx={{ cursor: "pointer" }}>
          <AddIcon fontSize="large" sx={{ width: "25px", color: `${PureLightThemeCaaS.colors.alpha.black[100]}` }} />
          <Typography variant="h6" pl={1} color={`${PureLightThemeCaaS.colors.alpha.black[100]}`} textAlign="center">
            Add payment method
          </Typography>
        </Box>
        <Divider
          sx={{
            margin: {
              xs: "auto",
              md: "0px",
            },
            width: "95%",
            bgcolor: "#999999",
            my: 0.5,
          }}
        /> */}
                {/* <Box
          onClick={() => onChangeContent("billingHistory")}
          display="flex"
          flexDirection="row"
          justifyContent="center"
          alignItems="center"
          pl={1}
          gap={1}
          sx={{ cursor: "pointer" }}
        >
          <ReceiptIcon fontSize="large" sx={{ width: "25px", color: `${PureLightThemeCaaS.colors.alpha.black[100]}` }} />
          <Typography variant="h6" pl={1} color={`${PureLightThemeCaaS.colors.alpha.black[100]}`} textAlign="center">
            Billing History
          </Typography>
        </Box>
        <Divider
          sx={{
            margin: {
              xs: "auto",
              md: "0px",
            },
            width: "95%",
            bgcolor: "#999999",
            my: 0.5,
          }}
        /> */}
                {/* make this next box disabled if running natively */}
                {/* {!isHybridPlatform ? ( */}
                <Box onClick={handleManageMembershipClick} display="flex" flexDirection="row" justifyContent="center" alignItems="center" pl={1} gap={1} sx={{ cursor: "pointer" }}>
                    {/* <HighlightOffIcon fontSize="large" sx={{ width: "25px", color: `${PureLightThemeCaaS.colors.alpha.black[100]}` }} /> */}
                    <AttachMoneyIcon fontSize="large" sx={{ width: "25px", color: `${PureLightThemeCaaS.colors.alpha.black[100]}` }} />
                    <Typography variant="h6" pl={1} color={`${PureLightThemeCaaS.colors.alpha.black[100]}`} textAlign="center">
                        Manage Membership
                    </Typography>
                </Box>
                {/* ) : (
                    <Box display="flex" flexDirection="row" justifyContent="center" alignItems="center" pl={1} gap={1} sx={{ cursor: "not-allowed" }}>
                        <AttachMoneyIcon fontSize="large" sx={{ width: "25px", color: `${PureLightThemeCaaS.colors.alpha.gray[50]}` }} />
                        <Typography variant="h6" pl={1} color={`${PureLightThemeCaaS.colors.alpha.gray[50]}`} textAlign="center">
                            Manage Membership
                        </Typography>
                    </Box>
                )} */}
                <Divider
                    sx={{
                        margin: {
                            xs: "auto",
                            md: "0px",
                        },
                        width: "100%",
                        bgcolor: "#999999",
                        my: 0.5,
                    }}
                />
                {/* Cancel Membership Box */}
                {showCancelMembership && (
                    <>
                        <Box
                            onClick={handleCancelMembershipClick}
                            display="flex"
                            flexDirection="row"
                            justifyContent="center"
                            alignItems="center"
                            height="35px"
                            pl={1}
                            gap={1}
                            sx={{
                                cursor: isLoadingCancelMembership ? "wait" : "pointer",
                                opacity: isLoadingCancelMembership ? 0.7 : 1,
                            }}
                        >
                            {isLoadingCancelMembership ? (
                                <CircularProgress size={25} sx={{ color: `${PureLightThemeCaaS.colors.alpha.orange[100]}` }} />
                            ) : (
                                <HighlightOffIcon fontSize="large" sx={{ width: "25px", color: `${PureLightThemeCaaS.colors.alpha.black[100]}` }} />
                            )}
                            <Typography variant="h6" pl={1} color={`${PureLightThemeCaaS.colors.alpha.black[100]}`} textAlign="center">
                                Cancel Membership
                            </Typography>
                        </Box>
                        <Divider
                            sx={{
                                margin: {
                                    xs: "auto",
                                    md: "0px",
                                },
                                width: "100%",
                                bgcolor: "#999999",
                                my: 0.5,
                            }}
                        />
                    </>
                )}

                {/* Logout Account Box */}
                <Box
                    onClick={() => {
                        doLogOut();
                        onClose();
                    }}
                    display="flex"
                    flexDirection="row"
                    justifyContent="center"
                    alignItems="center"
                    pl={1}
                    gap={1}
                    sx={{ cursor: "pointer" }}
                >
                    <LogOutButton color={"#000000"} />
                    <Typography variant="h6" pl={1} color={`${PureLightThemeCaaS.colors.alpha.black[100]}`} textAlign="center">
                        Log Out
                    </Typography>
                </Box>
                <Divider
                    sx={{
                        margin: {
                            xs: "auto",
                            md: "0px",
                        },
                        width: "100%",
                        bgcolor: "#999999",
                        my: 0.5,
                    }}
                />


                {/* Delete Account Box */}
                <Box
                    onClick={() => {
                        onClose();
                        onDeleteAccountClick?.();
                    }}
                    display="flex"
                    flexDirection="row"
                    justifyContent="center"
                    alignItems="center"
                    pl={1}
                    gap={1}
                    sx={{ cursor: "pointer" }}
                >
                    <DeleteAccountButton color={"#000000"} />
                    <Typography variant="h6" pl={1} color={`${PureLightThemeCaaS.colors.alpha.black[100]}`} textAlign="center">
                        Delete Account
                    </Typography>
                </Box>

                <Divider
                    sx={{
                        margin: {
                            xs: "auto",
                            md: "0px",
                        },
                        width: "100%",
                        bgcolor: "#999999",
                        my: 0.5,
                    }}
                />
            </Box>
        </>
    );
};
